import type { UserPublic } from '@repo/types/users';

import { useGetMyChildren } from '@repo/api/parent/useGetMyChildren';
import { TriangleAlertIcon } from 'lucide-react';

import { ChildAvatar, getChildFullName } from '@/components/features/parent/ChildAvatar';
import ParentPageHeader from '@/components/features/parent/layout/ParentPageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState, EmptyStateContent, EmptyStateDescription } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';

export default function ParentHomePage() {
  const { data: children = [], isPending, isError, errorMessage } = useGetMyChildren();

  return (
    <div className={'base-direction-col'}>
      <div className={'flex w-full grow flex-col gap-6'}>
        <ParentPageHeader title={'Moje dzieci'} />

        {isPending ? (
          <div className={'flex flex-col gap-4'}>
            <Skeleton className={'h-20 w-full'} />
            <Skeleton className={'h-20 w-full'} />
          </div>
        ) : null}

        {isError ? (
          <Alert variant={'destructive'}>
            <TriangleAlertIcon />
            <AlertDescription>{errorMessage.detail}</AlertDescription>
          </Alert>
        ) : null}

        {!isPending && !isError && !children.length ? (
          <EmptyState>
            <EmptyStateContent>
              <EmptyStateDescription>Brak przypisanych dzieci. Skontaktuj się ze szkołą, aby powiązać konto.</EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        ) : null}

        {children.length ? (
          <ul className={'flex flex-col gap-3'}>
            {children.map((child) => (
              <ChildCard key={child.id} child={child} />
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

function ChildCard({ child }: { child: UserPublic }) {
  return (
    <li className={'border-border-primary bg-background flex items-center gap-3 rounded-xl border p-4'}>
      <ChildAvatar child={child} className={'size-10'} />
      <div className={'flex flex-col'}>
        <span className={'font-medium'}>{getChildFullName(child)}</span>
        <span className={'text-muted-foreground text-sm'}>{child.email}</span>
      </div>
    </li>
  );
}
